import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Hero from '../components/Hero';
import SEO from '../components/SEO';
import './Info.css';
import newsService from '../services/newsService';
import caseService from '../services/caseService';
import resolveImageUrl from '../utils/imageResolver';

const Info = () => {
    // Read query params ?type=news|cases&id=NUMBER&from=about
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.get('type');
    const idParam = searchParams.get('id');
    const from = searchParams.get('from');

    const [contentData, setContentData] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!type || !idParam) {
            setLoaded(true);
            setContentData(null);
            return;
        }

        let alive = true;
        (async () => {
            try {
                let data = null;
                if (type === 'news') {
                    data = await newsService.getListObjectDataById(idParam);
                } else if (type === 'cases') {
                    data = await caseService.getListObjectDataById(idParam);
                }
                if (!alive) return;
                setContentData(data || null);
            } catch (_) {
                if (!alive) return;
                setContentData(null);
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [type, idParam]);

    if (!loaded) return <div className="text-center mt-5">Loading...</div>;
    if (!contentData || typeof contentData !== 'object') return <div className="text-center mt-5">Page not found</div>;

    const subtitle = type === 'news' ? '最新資訊詳情' : '成功故事詳情';
    const pageTitle = contentData.title || (type === 'news' ? '最新資訊' : '成功案例');
    const pageDescription = contentData.excerpt || contentData.description || (type === 'news' ? '最新留學與移民資訊' : '成功案例分享');
    const canonicalUrl = `https://liuyanghorizon.com${location.pathname}${location.search}`;
    const ogImage = contentData.image || 'https://liuyanghorizon.com/images/logo.png';

    return (
        <div className="info-page">
            <SEO
                title={`${pageTitle} - 留洋教育科技公司`}
                description={pageDescription}
                keywords={type === 'news' ? '留學資訊,移民資訊,最新資訊' : '成功案例,留學案例,移民案例'}
                canonical={canonicalUrl}
                ogImage={ogImage}
            />
            <Hero
                title={contentData.title || ''}
                subtitle={subtitle}
                imageUrl={resolveImageUrl(contentData.image || '', { width: 1200, height: 400, text: contentData.title || '', keywords: [subtitle] })}
            />
            <section className="info-content">
                <div className="container">
                    <div className="info-article card shadow-sm mx-auto">
                        <div className="card-body">
                            <div className="info-actions">
                                <Link 
                                    to={
                                        from === 'about' && type === 'cases' ? '/about#study-info-cases' :
                                        from === 'about' && type === 'news' ? '/about#education-column' :
                                        '/about#education-column'
                                    } 
                                    className="info-back" 
                                    aria-label={
                                        from === 'about' && type === 'cases' ? '返回成功案例列表' :
                                        from === 'about' && type === 'news' ? '返回教育信息专栏' :
                                        '返回最新資訊與案例頁'
                                    }
                                >
                                    <i className="fas fa-arrow-left"></i> 返回
                                </Link>
                            </div>
                            <div className="info-meta">{contentData.date || ''}</div>
                            <div className="info-text">
                                {contentData.content && Array.isArray(contentData.content) ? (
                                    contentData.content.map((item, index) => {
                                        if (!item || typeof item !== 'object') {
                                            return null;
                                        }
                                        
                                        if (item.type === 'text' && item.content) {
                                            const textContent = typeof item.content === 'string' 
                                                ? item.content 
                                                : String(item.content || '');
                                            return (
                                                <p key={index} className="info-paragraph">
                                                    {textContent}
                                                </p>
                                            );
                                        } else if (item.type === 'image' && item.content) {
                                            const imageUrl = typeof item.content === 'string' 
                                                ? item.content 
                                                : String(item.content || '');
                                            return (
                                                <div key={index} className="info-image-wrapper">
                                                    <img
                                                        src={resolveImageUrl(imageUrl, {
                                                            width: 1200,
                                                            height: 600,
                                                            text: contentData.title || '',
                                                            keywords: [subtitle]
                                                        })}
                                                        alt={`${contentData.title || 'Image'} - Image ${index + 1}`}
                                                        className="info-full-width-image"
                                                        loading="lazy"
                                                    />
                                                </div>
                                            );
                                        }
                                        return null;
                                    })
                                ) : (
                                    // Fallback for old string format
                                    <p>{typeof contentData.content === 'string' ? contentData.content : String(contentData.content || '')}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Info;